// Singleton serial queue for SBB travel time requests.
// Ensures requests are sent one at a time to avoid rate limiting (429).
// Priority requests are placed at the front of the queue.

const INTER_REQUEST_DELAY_MS = 300;

let queue = [];
let processing = false;

async function processNext() {
  if (processing || queue.length === 0) return;
  processing = true;

  const { fn, resolve, reject } = queue.shift();
  try {
    const result = await fn();
    resolve(result);
  } catch (err) {
    reject(err);
  } finally {
    await new Promise((r) => setTimeout(r, INTER_REQUEST_DELAY_MS));
    processing = false;
    processNext();
  }
}

/**
 * Enqueue a travel time request.
 * @param {() => Promise} fn          - async function that performs the API call
 * @param {() => boolean} isCancelled - returns true if the caller no longer needs the result
 * @param {boolean} priority          - if true, placed at the front of the queue
 * @returns {Promise} resolves/rejects with the result of fn()
 */
export function enqueueRequest(fn, isCancelled, priority = false) {
  return new Promise((resolve, reject) => {
    const entry = {
      fn: async () => {
        if (isCancelled()) {
          resolve(null);
          return null;
        }
        return fn();
      },
      resolve,
      reject,
    };
    if (priority) {
      queue.unshift(entry);
    } else {
      queue.push(entry);
    }
    processNext();
  });
}

/** Flush all pending (not yet started) queue entries, e.g. on filter reset. */
export function clearQueue() {
  queue = [];
}
